import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import RoleSelect from "./RoleSelect";
import "./RoleSelectModal.css";

/**
 * Wraps RoleSelect in a portal'd modal with a backdrop-blur entry, scaled
 * panel, and per-half slide-in. Used by IlanVerLanding so visiting
 * /ilan-ver pops the role picker over the homepage instead of replacing it.
 */
function RoleSelectModal({ open, onClose }) {
  // Three states needed to orchestrate the entry transition reliably:
  //   mounted   — portal nodes exist in the DOM
  //   animateIn — `--in` class applied (drives CSS transitions)
  //
  // The order matters: we must render the portal first WITHOUT the `--in`
  // class so the browser commits the "out" CSS values, THEN flip to `--in`
  // on a later frame so the browser sees a state change to transition from.
  // React batches state updates within an effect, so doing both in one
  // useEffect causes the browser to paint only the final state — the modal
  // appears to pop in with no animation. Double-RAF guarantees the initial
  // paint lands before the class flip.
  const [mounted, setMounted] = useState(open);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setAnimateIn(false);
    const t = setTimeout(() => setMounted(false), 460);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!mounted || !open) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setAnimateIn(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [mounted, open]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <div className={`rsModal${animateIn ? " rsModal--in" : ""}`} role="dialog" aria-modal="true">
      <div className="rsModal__backdrop" onClick={onClose} />
      <div className="rsModal__spotlight" aria-hidden="true" />
      <div className="rsModal__panel" onClick={(e) => e.stopPropagation()}>
        <RoleSelect inModal onClose={onClose} />
      </div>
    </div>,
    document.body
  );
}

export default RoleSelectModal;
